import { Button, ConfirmModal, Dropdown } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import {
  useGroup,
  useUpdateGroupTargets,
} from '@/features/project/groups/_hooks/group';
import { BulkCopyFormDrawer } from '@/features/project/groups/group-detail/_components/group-in-projects/BulkCopyFormDrawer';
import { BulkLockDrawer } from '@/features/project/groups/group-detail/_components/group-in-projects/BulkLockDrawer';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { DownOutlined } from '@ant-design/icons';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import {
  useBulkCopy,
  useBulkLock,
  useBulkPublish,
  useBulkUnlock,
  useBulkUnpublish,
  useGroupProjects,
} from '../../_hooks/group';
import { BulkPublishDrawer } from './BulkPublishDrawer';

type ModalState =
  | {
      open: false;
    }
  | {
      open: true;
      type: ModalStateType;
    };

type ModalStateType =
  | 'release'
  | 'lock'
  | 'unlock'
  | 'copy'
  | 'publish'
  | 'unpublish';

export const GroupInProjectsBulkActionsButton: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id } = useId();
  const group = useGroup(id, { projectIds: true });
  const me = useMe();
  const [openModal, setOpenModal] = useState<ModalState>({ open: false });
  const { mutateAsync: bulkCopyAsync } = useBulkCopy(id);
  const { mutateAsync: bulkLockAsync } = useBulkLock(id);
  const { mutateAsync: bulkUnlockAsync } = useBulkUnlock(id);
  const { mutateAsync: bulkPublishAsync } = useBulkPublish(id);
  const { mutateAsync: bulkUnpublishAsync } = useBulkUnpublish(id);
  const { mutateAsync: updateGroupTargetsAsync } = useUpdateGroupTargets(id);
  const { searchQueryStates } = useProjectsPageSearchQueryStates();
  const [isLoading, setIsLoading] = useState(false);

  const groupProjects = useGroupProjects({
    id,
    page: searchQueryStates.groupInProjectsPage,
    ordering: searchQueryStates.groupInProjectsOrdering,
  });

  const canBulkControl = useMemo(() => {
    if (!group.data?.groupTargets.length || !groupProjects.data?.count) {
      return false;
    }
    return group.data.groupTargets.length === groupProjects.data.count;
  }, [group.data?.groupTargets.length, groupProjects.data?.count]);

  const editPermission =
    (group.data &&
      me.data?.organizationIds.includes(group.data.organizationId)) ??
    false;

  if (
    !group.data ||
    !me.data?.organizationIds.includes(group.data.organizationId)
  ) {
    return null;
  }

  return (
    <>
      <Dropdown
        menu={{
          items: [
            editPermission
              ? {
                  key: 'release',
                  label: t('group-project-bulk-operation-release-all-projects'),
                  disabled:
                    !groupProjects.data ||
                    groupProjects.data.results.length === 0,
                }
              : null,
            ...(me.data.role.lockAbility
              ? [
                  {
                    key: 'lock',
                    label: t('group-project-bulk-operation-lock-projects'),
                    disabled: !canBulkControl,
                  },
                  {
                    key: 'unlock',
                    label: t('group-project-bulk-operation-unlock-projects'),
                    disabled: !canBulkControl,
                  },
                ]
              : []),
            ...(me.data.role.publishAbility
              ? [
                  {
                    key: 'publish',
                    label: t('group-project-bulk-operation-publish-projects'),
                    disabled: !canBulkControl,
                  },
                  {
                    key: 'unpublish',
                    label: t('group-project-bulk-operation-unpublish-projects'),
                    disabled: !canBulkControl,
                  },
                ]
              : []),
            ...(me.data.role.continueAbility
              ? [
                  {
                    key: 'copy',
                    label: t('group-project-bulk-operation-copy-projects'),
                    disabled: !canBulkControl,
                  },
                ]
              : []),
          ],
          onClick: (e) => {
            setOpenModal({ open: true, type: e.key as ModalStateType });
          },
        }}
      >
        <Button>
          {t('group-project-bulk-operation')}
          <DownOutlined />
        </Button>
      </Dropdown>

      <BulkLockDrawer
        {...(openModal.open &&
        (openModal.type === 'lock' || openModal.type === 'unlock')
          ? {
              type: openModal.type,
              open: true,
            }
          : {
              type: undefined,
              open: false,
            })}
        onSubmit={async () => {
          invariant(
            openModal.open &&
              (openModal.type === 'lock' || openModal.type === 'unlock'),
            t('unreachable-code'),
          );
          const res =
            openModal.type === 'lock'
              ? await bulkLockAsync()
              : await bulkUnlockAsync();
          res.requestedCount === 0
            ? openMessage('warning', t('message-no-update'))
            : openMessage(
                'success',
                t('message-bulk-process-requested', {
                  requestedCount: res.requestedCount,
                }),
              );
          setOpenModal({ open: false });
        }}
        onClose={() => {
          setOpenModal({ open: false });
        }}
      />

      <BulkPublishDrawer
        {...(openModal.open &&
        (openModal.type === 'publish' || openModal.type === 'unpublish')
          ? {
              type: openModal.type,
              open: true,
            }
          : {
              type: undefined,
              open: false,
            })}
        onSubmit={async () => {
          invariant(
            openModal.open &&
              (openModal.type === 'publish' || openModal.type === 'unpublish'),
            t('unreachable-code'),
          );

          const res =
            openModal.type === 'publish'
              ? await bulkPublishAsync()
              : await bulkUnpublishAsync();
          await Promise.all([
            openMessage(
              'success',
              t('message-bulk-process-requested', {
                requestedCount: res.requestedCount,
              }),
            ),
            (async () => {
              setOpenModal({ open: false });
            })(),
          ]);
        }}
        onClose={() => {
          setOpenModal({ open: false });
        }}
      />

      <BulkCopyFormDrawer
        isLoading={isLoading}
        open={openModal.open && openModal.type === 'copy'}
        fiscalYear={group.data.fiscalYear}
        onSubmit={async (values) => {
          setIsLoading(true);
          try {
            const res = await bulkCopyAsync(values);
            res.requestedCount === 0
              ? openMessage('warning', t('message-no-update'))
              : openMessage(
                  'success',
                  t('message-bulk-process-requested', {
                    requestedCount: res.requestedCount,
                  }),
                );
            setOpenModal({ open: false });
          } catch {
            openMessage('error', t('unknown-error'));
          } finally {
            setIsLoading(false);
          }
        }}
        onClose={() => {
          setOpenModal({ open: false });
        }}
      />

      <ConfirmModal
        open={openModal.open && openModal.type === 'release'}
        title={t(
          'group-project-bulk-operation-release-all-projects-modal-title',
        )}
        disclaimerText={t(
          'group-project-bulk-operation-release-all-projects-confirmation-message',
        )}
        onOk={async () => {
          await updateGroupTargetsAsync([]);
          await Promise.all([
            openMessage('success', t('message-update-completed')),
            (async () => {
              setOpenModal({ open: false });
            })(),
          ]);
        }}
        onCancel={() => {
          setOpenModal({ open: false });
        }}
        skipCheck
      />
    </>
  );
};
